import { X, Save } from 'lucide-react';
import { colors } from '../../styles/colors';
import { MOIS, TRIMESTRES } from '../../utils/constants';
import { useChargeForm } from '../../hooks/useChargeForm';

/**
 * ChargeFormModal - Modal de formulaire de charge (création/édition)
 *
 * Refactorisé pour utiliser:
 * - useChargeForm: hook de gestion du formulaire
 * - MOIS, TRIMESTRES: constantes centralisées
 */
function ChargeFormModal({
  show,
  onClose,
  onSubmit,
  mode = 'create',
  charge = null,
  filterAnnee,
  consultants = [],
  existingCharges = []
}) {
  const {
    formData,
    submitting,
    nomError,
    handleInputChange,
    handleSubmit
  } = useChargeForm(show, mode, charge, filterAnnee, existingCharges, onSubmit, onClose);

  if (!show) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <ModalHeader mode={mode} charge={charge} onClose={onClose} />

        {/* Formulaire */}
        <form onSubmit={handleSubmit}>
          {/* Nom */}
          <NomField
            value={formData.nom}
            onChange={handleInputChange}
            error={nomError}
          />

          {/* Type */}
          {mode !== 'edit-group' && (
            <TypeField
              value={formData.type}
              onChange={handleInputChange}
            />
          )}

          {/* Année */}
          {mode !== 'edit-group' && (
            <AnneeField
              value={formData.annee}
              onChange={handleInputChange}
            />
          )}

          {/* Trimestre pour charges trimestrielles */}
          {mode !== 'edit-group' && formData.type === 'Trimestrielle' && (
            <TrimestreSection
              mode={mode}
              formData={formData}
              onChange={handleInputChange}
            />
          )}

          {/* Mois pour charges mensuelles/exceptionnelles */}
          {mode !== 'edit-group' && (formData.type === 'Mensuelle' || formData.type === 'Exceptionnelle') && (
            <MoisSection
              mode={mode}
              formData={formData}
              onChange={handleInputChange}
            />
          )}

          {/* Montant */}
          <MontantField
            value={formData.montant}
            onChange={handleInputChange}
          />

          {/* Consultant */}
          <ConsultantField
            value={formData.consultant}
            onChange={handleInputChange}
            consultants={consultants}
          />

          {/* Boutons */}
          <FormButtons
            onClose={onClose}
            submitting={submitting}
            disabled={!!nomError}
          />
        </form>
      </div>
    </div>
  );
}

// === SOUS-COMPOSANTS ===

function ModalHeader({ mode, charge, onClose }) {
  const getTitle = () => {
    switch (mode) {
      case 'edit-group': return 'Modifier le groupe de charges';
      case 'edit': return 'Modifier la charge';
      default: return 'Nouvelle charge';
    }
  };

  return (
    <div style={headerStyle}>
      <div>
        <h2 style={titleStyle}>{getTitle()}</h2>
        {mode === 'edit-group' && charge && (
          <p style={subtitleStyle}>
            Cette modification s'appliquera à toutes les {charge.charges?.length} charges du groupe
          </p>
        )}
      </div>
      <button onClick={onClose} style={closeButtonStyle}>
        <X size={24} />
      </button>
    </div>
  );
}

function FormField({ label, required, error, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={labelStyle}>
        {label} {required && '*'}
      </label>
      {children}
      {error && <p style={errorTextStyle}>{error}</p>}
    </div>
  );
}

function NomField({ value, onChange, error }) {
  return (
    <FormField label="Nom de la charge" required error={error}>
      <input
        type="text"
        name="nom"
        value={value}
        onChange={onChange}
        required
        style={{
          ...inputStyle,
          borderColor: error ? '#dc2626' : colors.secondary.lightGray,
          backgroundColor: error ? '#fef2f2' : 'white'
        }}
      />
    </FormField>
  );
}

function TypeField({ value, onChange }) {
  const typeOptions = [
    { value: 'Trimestrielle', label: 'Trimestrielle' },
    { value: 'Annuelle', label: 'Annuelle' },
    { value: 'Mensuelle', label: 'Mensuelle' },
    { value: 'Exceptionnelle', label: 'Exceptionnelle' }
  ];

  return (
    <FormField label="Type" required>
      <select
        name="type"
        value={value}
        onChange={onChange}
        required
        style={inputStyle}
      >
        {typeOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </FormField>
  );
}

function AnneeField({ value, onChange }) {
  return (
    <FormField label="Année" required>
      <input
        type="number"
        name="annee"
        value={value}
        onChange={onChange}
        required
        min="2000"
        max="2100"
        style={inputStyle}
      />
    </FormField>
  );
}

function TrimestreSection({ mode, formData, onChange }) {
  return (
    <>
      {mode === 'create' && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              name="tousLesTrimestres"
              checked={formData.tousLesTrimestres}
              onChange={onChange}
              style={{ cursor: 'pointer' }}
            />
            Créer pour tous les trimestres de l'année
          </label>
        </div>
      )}

      {!formData.tousLesTrimestres && (
        <FormField label="Trimestre" required>
          <select
            name="trimestre"
            value={formData.trimestre}
            onChange={onChange}
            required={!formData.tousLesTrimestres}
            style={inputStyle}
          >
            <option value="">-- Sélectionner --</option>
            {TRIMESTRES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </FormField>
      )}
    </>
  );
}

function MoisSection({ mode, formData, onChange }) {
  const isMensuelle = formData.type === 'Mensuelle';
  const isRequired = isMensuelle && !formData.tousLesMois;

  return (
    <>
      {isMensuelle && mode === 'create' && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              name="tousLesMois"
              checked={formData.tousLesMois}
              onChange={onChange}
              style={{ cursor: 'pointer' }}
            />
            Créer pour tous les mois de l'année
          </label>
        </div>
      )}

      {!formData.tousLesMois && (
        <FormField label={`Mois${isMensuelle ? '' : ' (optionnel)'}`} required={isRequired}>
          <select
            name="mois"
            value={formData.mois}
            onChange={onChange}
            required={isRequired}
            style={inputStyle}
          >
            <option value="">-- Sélectionner --</option>
            {MOIS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </FormField>
      )}
    </>
  );
}

function MontantField({ value, onChange }) {
  return (
    <FormField label="Montant (€)" required>
      <input
        type="number"
        name="montant"
        value={value}
        onChange={onChange}
        required
        step="0.01"
        style={inputStyle}
      />
    </FormField>
  );
}

function ConsultantField({ value, onChange, consultants }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <label style={labelStyle}>
        Consultant (optionnel)
      </label>
      <select
        name="consultant"
        value={value}
        onChange={onChange}
        style={inputStyle}
      >
        <option value="">-- Tous les consultants --</option>
        {consultants.map(c => (
          <option key={c.id} value={c.id}>{c.nom}</option>
        ))}
      </select>
    </div>
  );
}

function FormButtons({ onClose, submitting, disabled }) {
  return (
    <div style={buttonsContainerStyle}>
      <button
        type="button"
        onClick={onClose}
        style={cancelButtonStyle}
      >
        Annuler
      </button>
      <button
        type="submit"
        disabled={submitting || disabled}
        style={{
          ...submitButtonStyle,
          cursor: (submitting || disabled) ? 'not-allowed' : 'pointer',
          opacity: (submitting || disabled) ? 0.7 : 1
        }}
      >
        <Save size={16} />
        {submitting ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </div>
  );
}

// === STYLES ===

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '0.5rem',
  padding: '2rem',
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem'
};

const titleStyle = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: colors.primary.gray,
  margin: 0
};

const subtitleStyle = {
  margin: '0.5rem 0 0 0',
  fontSize: '0.875rem',
  color: colors.secondary.mediumGray
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.secondary.mediumGray
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.primary.gray
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.primary.gray,
  cursor: 'pointer'
};

const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: `1px solid ${colors.secondary.lightGray}`,
  borderRadius: '0.25rem',
  fontSize: '0.875rem'
};

const errorTextStyle = {
  margin: '0.25rem 0 0 0',
  fontSize: '0.75rem',
  color: '#dc2626'
};

const buttonsContainerStyle = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-end'
};

const cancelButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: colors.secondary.mediumGray,
  color: 'white',
  border: 'none',
  borderRadius: '0.25rem',
  cursor: 'pointer',
  fontSize: '0.875rem'
};

const submitButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: colors.primary.gold,
  color: 'white',
  border: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

export default ChargeFormModal;
